/**
 * Cursor cosmetic overlay rendering (Shop preview + in-game).
 * Art canvas is 128×128 with a reference cursor of radius 14 at the center
 * (Shop Creator). Overlay size in-game is scaled so that guide maps 1:1 to the play cursor.
 */

import { ANIM_FRAME_MS } from "./shop-creator-fx";
import {
  decodeMatchShadeByte,
  shadeHexColor,
} from "./cosmetic-recolor";

export const COSMETIC_ART_SIZE = 128;
export const COSMETIC_REF_CURSOR_R = 14;

const imageCache = new Map<string, HTMLImageElement>();
const loadPromises = new Map<string, Promise<HTMLImageElement | null>>();
/** Built tint canvases: key = src|tint|outline|match */
const tintedCanvasCache = new Map<string, HTMLCanvasElement>();
/** Scaled pixel-art canvases: key = artKey|destSize */
const scaledArtCache = new Map<string, HTMLCanvasElement>();

export type EquippedCosmeticLayer = {
  imageData: string;
  category: "hat" | "face" | "cursor" | "cursor_color" | "trail" | "banner" | "theme";
  /** Animated overlays drawn on top of imageData; static art stays put. */
  animationFrames?: string[] | null;
  /** Original art before match/custom bake — used for live forced rematch. */
  sourceImageData?: string | null;
  outlineMaskData?: string | null;
  matchCursorMaskData?: string | null;
  matchCursorMaskFrames?: string[] | null;
  sourceAnimationFrames?: string[] | null;
  trailMode?: "multiColor" | "particle" | null;
  trailColors?: string[] | null;
  /** Hide filled play cursor; art becomes the visual (hitbox unchanged). */
  newCursor?: boolean;
};

let equippedCosmeticLayers: EquippedCosmeticLayer[] = [];
/** Equipped trail cosmetic (at most one); drawn via trail renderer, not cursor overlay. */
let equippedTrailLayer: EquippedCosmeticLayer | null = null;
/** Equipped banner (profile only — never drawn on play cursor). */
let equippedBannerLayer: EquippedCosmeticLayer | null = null;
/** Full shop catalog for resolving other players' equipped cosmetic ids in multiplayer. */
let shopCosmeticCatalog = new Map<string, EquippedCosmeticLayer>();

function normalizeCategory(raw: string | null | undefined): EquippedCosmeticLayer["category"] {
  if (
    raw === "hat" ||
    raw === "face" ||
    raw === "cursor_color" ||
    raw === "trail" ||
    raw === "banner" ||
    raw === "theme"
  ) {
    return raw;
  }
  if (raw === "cursor" || raw === "cursor_body" || raw === "body") return "cursor";
  return "cursor";
}

/** Cursor section always rematches in forced-color modes (outlines stay). */
export function isCursorLiveTintCategory(cat: string | null | undefined): boolean {
  return cat === "cursor_color";
}

function preloadLayerImages(layer: EquippedCosmeticLayer) {
  void loadCosmeticImage(layer.imageData);
  if (layer.sourceImageData) void loadCosmeticImage(layer.sourceImageData);
  if (layer.outlineMaskData) void loadCosmeticImage(layer.outlineMaskData);
  if (layer.matchCursorMaskData) void loadCosmeticImage(layer.matchCursorMaskData);
  if (layer.animationFrames?.length) {
    for (const f of layer.animationFrames) void loadCosmeticImage(f);
  }
  if (layer.sourceAnimationFrames?.length) {
    for (const f of layer.sourceAnimationFrames) void loadCosmeticImage(f);
  }
  if (layer.matchCursorMaskFrames?.length) {
    for (const f of layer.matchCursorMaskFrames) void loadCosmeticImage(f);
  }
}

export function setShopCosmeticCatalog(
  entries: Array<{
    id: string;
    imageData: string;
    category?: string | null;
    animationFrames?: string[] | null;
    outlineMaskData?: string | null;
    matchCursorMaskData?: string | null;
    matchCursorMaskFrames?: string[] | null;
    trailMode?: "multiColor" | "particle" | null;
    trailColors?: string[] | null;
    newCursor?: boolean | null;
  }>,
) {
  const next = new Map<string, EquippedCosmeticLayer>();
  for (const e of entries) {
    if (!e?.id || typeof e.imageData !== "string" || !e.imageData) continue;
    const layer: EquippedCosmeticLayer = {
      imageData: e.imageData,
      sourceImageData: e.imageData,
      category: normalizeCategory(e.category),
      animationFrames:
        Array.isArray(e.animationFrames) && e.animationFrames.length
          ? e.animationFrames.filter((f) => typeof f === "string" && f.length > 0).slice(0, 10)
          : null,
      sourceAnimationFrames:
        Array.isArray(e.animationFrames) && e.animationFrames.length
          ? e.animationFrames.filter((f) => typeof f === "string" && f.length > 0).slice(0, 10)
          : null,
      outlineMaskData: e.outlineMaskData ?? null,
      matchCursorMaskData: e.matchCursorMaskData ?? null,
      matchCursorMaskFrames:
        Array.isArray(e.matchCursorMaskFrames) && e.matchCursorMaskFrames.length
          ? e.matchCursorMaskFrames.filter((f) => typeof f === "string" && f.length > 0).slice(0, 10)
          : null,
      trailMode: e.trailMode === "multiColor" || e.trailMode === "particle" ? e.trailMode : null,
      trailColors: Array.isArray(e.trailColors)
        ? e.trailColors.filter((c): c is string => typeof c === "string").slice(0, 10)
        : null,
      newCursor: !!e.newCursor,
    };
    next.set(e.id, layer);
    preloadLayerImages(layer);
  }
  shopCosmeticCatalog = next;
}

export function setEquippedCosmeticLayers(layers: EquippedCosmeticLayer[]) {
  const all = layers.filter((l) => typeof l.imageData === "string" && l.imageData.length > 0);
  equippedTrailLayer = all.find((l) => l.category === "trail") ?? null;
  equippedBannerLayer = all.find((l) => l.category === "banner") ?? null;
  equippedCosmeticLayers = all.filter(
    (l) => l.category !== "trail" && l.category !== "banner" && l.category !== "theme",
  );
  for (const layer of all) preloadLayerImages(layer);
}

export function getEquippedTrailLayer(): EquippedCosmeticLayer | null {
  return equippedTrailLayer;
}

export function getEquippedBannerLayer(): EquippedCosmeticLayer | null {
  return equippedBannerLayer;
}

/** True when an equipped New Cursor cosmetic hides the filled play cursor. */
export function equippedHidesBaseCursor(): boolean {
  return equippedCosmeticLayers.some((l) => !!l.newCursor);
}

/** True when any of the given shop cosmetic ids is a New Cursor item. */
export function cosmeticIdsHideBaseCursor(ids: string[] | null | undefined): boolean {
  if (!ids?.length) return false;
  for (const id of ids) {
    const layer = shopCosmeticCatalog.get(id);
    if (!layer || layer.category === "theme") continue;
    if (layer.newCursor) return true;
  }
  return false;
}

export function getTrailLayerForIds(ids: string[] | null | undefined): EquippedCosmeticLayer | null {
  if (!ids?.length) return null;
  for (const id of ids) {
    const layer = shopCosmeticCatalog.get(id);
    if (layer?.category === "trail") return layer;
  }
  return null;
}

export function setEquippedCosmeticImageDatas(imageDatas: string[]) {
  setEquippedCosmeticLayers(
    imageDatas
      .filter((d) => typeof d === "string" && d.length > 0)
      .map((imageData) => ({ imageData, category: "cursor" as const })),
  );
}

/** @deprecated prefer setEquippedCosmeticImageDatas */
export function setEquippedCosmeticImageData(imageData: string | null) {
  setEquippedCosmeticImageDatas(imageData ? [imageData] : []);
}

export function getEquippedCosmeticImageDatas(): string[] {
  return equippedCosmeticLayers.map((l) => l.imageData);
}

/** @deprecated prefer getEquippedCosmeticImageDatas */
export function getEquippedCosmeticImageData(): string | null {
  return equippedCosmeticLayers[0]?.imageData ?? null;
}

/** World-space size of the full 128 art canvas for a given play cursor radius. */
export function cosmeticOverlaySizeForCursor(cursorRadius: number): number {
  return Math.max(1, cursorRadius * (COSMETIC_ART_SIZE / COSMETIC_REF_CURSOR_R));
}

/** Integer destination box so pixel art lands on the canvas grid. */
function cosmeticDrawBox(x: number, y: number, cursorRadius: number, cosmeticScale = 1) {
  const size = Math.max(1, Math.round(cosmeticOverlaySizeForCursor(cursorRadius) * cosmeticScale));
  return {
    size,
    dx: Math.round(x - size / 2),
    dy: Math.round(y - size / 2),
  };
}

function artSourceSize(source: HTMLImageElement | HTMLCanvasElement): { w: number; h: number } {
  if (source instanceof HTMLCanvasElement) {
    return { w: source.width, h: source.height };
  }
  return {
    w: source.naturalWidth || source.width || COSMETIC_ART_SIZE,
    h: source.naturalHeight || source.height || COSMETIC_ART_SIZE,
  };
}

function readArtPixels(source: HTMLImageElement | HTMLCanvasElement): ImageData {
  const { w, h } = artSourceSize(source);
  if (source instanceof HTMLCanvasElement) {
    const ctx = source.getContext("2d")!;
    return ctx.getImageData(0, 0, w, h);
  }
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const ctx = c.getContext("2d")!;
  ctx.clearRect(0, 0, w, h);
  ctx.drawImage(source, 0, 0);
  return ctx.getImageData(0, 0, w, h);
}

/**
 * Scale cosmetic art for in-game draw.
 * Upscale: nearest-neighbor. Downscale: max-alpha per dest cell so thin pixels survive
 * (browser NN point-sampling drops 1px details when shrinking 128→~cursor size).
 */
function scaleCosmeticArt(
  source: HTMLImageElement | HTMLCanvasElement,
  destSize: number,
  cacheKey: string,
): HTMLCanvasElement {
  const key = `${cacheKey}|${destSize}`;
  const hit = scaledArtCache.get(key);
  if (hit) return hit;
  if (scaledArtCache.size > 96) scaledArtCache.clear();

  const { w: sw, h: sh } = artSourceSize(source);
  const out = document.createElement("canvas");
  out.width = destSize;
  out.height = destSize;
  const octx = out.getContext("2d")!;

  if (destSize === sw && destSize === sh) {
    octx.drawImage(source, 0, 0);
    scaledArtCache.set(key, out);
    return out;
  }

  if (destSize >= sw && destSize >= sh) {
    octx.imageSmoothingEnabled = false;
    octx.drawImage(source, 0, 0, destSize, destSize);
    scaledArtCache.set(key, out);
    return out;
  }

  const src = readArtPixels(source);
  const sd = src.data;
  const outImg = octx.createImageData(destSize, destSize);
  const od = outImg.data;
  for (let dy = 0; dy < destSize; dy++) {
    const y0 = Math.floor((dy * sh) / destSize);
    const y1 = Math.max(y0 + 1, Math.ceil(((dy + 1) * sh) / destSize));
    for (let dx = 0; dx < destSize; dx++) {
      const x0 = Math.floor((dx * sw) / destSize);
      const x1 = Math.max(x0 + 1, Math.ceil(((dx + 1) * sw) / destSize));
      let bestA = -1;
      let br = 0;
      let bg = 0;
      let bb = 0;
      let ba = 0;
      for (let sy = y0; sy < y1 && sy < sh; sy++) {
        for (let sx = x0; sx < x1 && sx < sw; sx++) {
          const i = (sy * sw + sx) * 4;
          const a = sd[i + 3];
          if (a > bestA) {
            bestA = a;
            br = sd[i];
            bg = sd[i + 1];
            bb = sd[i + 2];
            ba = a;
          }
        }
      }
      const oi = (dy * destSize + dx) * 4;
      od[oi] = br;
      od[oi + 1] = bg;
      od[oi + 2] = bb;
      od[oi + 3] = ba;
    }
  }
  octx.putImageData(outImg, 0, 0);
  scaledArtCache.set(key, out);
  return out;
}

function drawScaledCosmeticArt(
  ctx: CanvasRenderingContext2D,
  source: HTMLImageElement | HTMLCanvasElement,
  x: number,
  y: number,
  cursorRadius: number,
  cacheKey: string,
  cosmeticScale = 1,
) {
  const { size, dx, dy } = cosmeticDrawBox(x, y, cursorRadius, cosmeticScale);
  const scaled = scaleCosmeticArt(source, size, cacheKey);
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(scaled, dx, dy);
}

/** Cursor radius so the cosmetic art fills `fillRatio` of the smaller canvas side. */
export function cosmeticCardCursorRadius(canvasSize: number, fillRatio = 0.96): number {
  const fill = Math.max(1, canvasSize * fillRatio);
  return fill / (COSMETIC_ART_SIZE / COSMETIC_REF_CURSOR_R);
}

export function loadCosmeticImage(imageData: string): Promise<HTMLImageElement | null> {
  if (!imageData) return Promise.resolve(null);
  const cached = imageCache.get(imageData);
  if (cached?.complete && cached.naturalWidth > 0) return Promise.resolve(cached);
  const pending = loadPromises.get(imageData);
  if (pending) return pending;

  const p = new Promise<HTMLImageElement | null>((resolve) => {
    const img = new Image();
    let settled = false;
    const finish = (value: HTMLImageElement | null) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      loadPromises.delete(imageData);
      resolve(value);
    };
    const timer = window.setTimeout(() => finish(null), 8_000);
    img.onload = () => {
      imageCache.set(imageData, img);
      finish(img);
    };
    img.onerror = () => finish(null);
    // Lite catalog uses /api/cosmetic-asset URLs; legacy uses data: or raw base64.
    if (
      imageData.startsWith("data:") ||
      imageData.startsWith("http://") ||
      imageData.startsWith("https://") ||
      imageData.startsWith("/")
    ) {
      img.src = imageData;
    } else {
      img.src = `data:image/png;base64,${imageData}`;
    }
  });
  loadPromises.set(imageData, p);
  return p;
}

export function getCachedCosmeticImage(imageData: string | null | undefined): HTMLImageElement | null {
  if (!imageData) return null;
  const img = imageCache.get(imageData);
  if (img?.complete && img.naturalWidth > 0) return img;
  void loadCosmeticImage(imageData);
  return null;
}

export async function preloadCosmeticImages(
  imageDatas: Array<string | null | undefined>,
): Promise<void> {
  await Promise.all(imageDatas.filter((d): d is string => !!d).map((d) => loadCosmeticImage(d)));
}

export function drawReferenceCursor(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  radius: number = COSMETIC_REF_CURSOR_R,
  fillColor = "#ffffff",
) {
  ctx.save();
  ctx.fillStyle = fillColor;
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "rgba(0,0,0,0.35)";
  ctx.lineWidth = Math.max(1, radius * 0.08);
  ctx.stroke();
  ctx.restore();
}

/**
 * Smooth (non-pixel) hitbox ring for New Cursor editing — editor canvas only.
 * Matches the real play cursor collision circle (radius = guide radius).
 */
export function drawReferenceCursorHitboxOutline(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  radius: number = COSMETIC_REF_CURSOR_R,
  strokeColor = "rgba(102, 255, 255, 0.95)",
) {
  ctx.save();
  ctx.imageSmoothingEnabled = true;
  ctx.strokeStyle = strokeColor;
  ctx.lineWidth = Math.max(1.25, radius * 0.1);
  ctx.setLineDash([]);
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.stroke();
  // Soft outer dash so the ring stays visible on light pixels.
  ctx.strokeStyle = "rgba(0, 0, 0, 0.55)";
  ctx.lineWidth = Math.max(0.75, radius * 0.05);
  ctx.setLineDash([Math.max(2, radius * 0.18), Math.max(2, radius * 0.14)]);
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

function hexToRgb(hex: string): [number, number, number] {
  const raw = String(hex || "").trim();
  const rgb = raw.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i);
  if (rgb) {
    return [
      Math.max(0, Math.min(255, Math.round(Number(rgb[1])))),
      Math.max(0, Math.min(255, Math.round(Number(rgb[2])))),
      Math.max(0, Math.min(255, Math.round(Number(rgb[3])))),
    ];
  }
  const h = raw.replace("#", "");
  const n = parseInt(h.length === 3 ? h.replace(/./g, (c) => c + c) : h, 16);
  if (!Number.isFinite(n)) return [255, 255, 255];
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function buildTintedKeepingOutline(
  base: HTMLImageElement,
  tintColor: string,
  outlineMask: HTMLImageElement | null,
  matchMask: HTMLImageElement | null,
  flatTintNonMatch: boolean,
): HTMLCanvasElement {
  const w = base.naturalWidth || base.width || COSMETIC_ART_SIZE;
  const h = base.naturalHeight || base.height || COSMETIC_ART_SIZE;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, w, h);
  ctx.drawImage(base, 0, 0, w, h);
  const img = ctx.getImageData(0, 0, w, h);
  const data = img.data;

  let outline: Uint8ClampedArray | null = null;
  let match: Uint8ClampedArray | null = null;
  if (outlineMask) {
    const oc = document.createElement("canvas");
    oc.width = w;
    oc.height = h;
    const octx = oc.getContext("2d")!;
    octx.clearRect(0, 0, w, h);
    octx.drawImage(outlineMask, 0, 0, w, h);
    outline = octx.getImageData(0, 0, w, h).data;
  }
  if (matchMask) {
    const mc = document.createElement("canvas");
    mc.width = w;
    mc.height = h;
    const mctx = mc.getContext("2d")!;
    mctx.clearRect(0, 0, w, h);
    mctx.drawImage(matchMask, 0, 0, w, h);
    match = mctx.getImageData(0, 0, w, h).data;
  }

  const flat = hexToRgb(tintColor);
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] === 0) continue;
    if (outline && outline[i + 3] > 0) continue;
    if (match && match[i + 3] > 0) {
      const shaded = shadeHexColor(tintColor, decodeMatchShadeByte(match[i]));
      const [tr, tg, tb] = hexToRgb(shaded);
      data[i] = tr;
      data[i + 1] = tg;
      data[i + 2] = tb;
      continue;
    }
    if (flatTintNonMatch || !match) {
      data[i] = flat[0];
      data[i + 1] = flat[1];
      data[i + 2] = flat[2];
    }
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

function getTintedLayerCanvas(
  baseSrc: string,
  tintColor: string,
  outlineSrc: string | null | undefined,
  matchSrc: string | null | undefined,
  flatTintNonMatch: boolean,
): HTMLCanvasElement | null {
  const base = getCachedCosmeticImage(baseSrc);
  if (!base) return null;
  const outline = outlineSrc ? getCachedCosmeticImage(outlineSrc) : null;
  const match = matchSrc ? getCachedCosmeticImage(matchSrc) : null;
  // Wait until masks are loaded when provided (avoid flashing wrong tint).
  if (outlineSrc && !outline) return null;
  if (matchSrc && !match) return null;
  const key = `${baseSrc}|${tintColor}|${outlineSrc || ""}|${matchSrc || ""}|${flatTintNonMatch ? 1 : 0}`;
  const hit = tintedCanvasCache.get(key);
  if (hit) return hit;
  if (tintedCanvasCache.size > 64) tintedCanvasCache.clear();
  const built = buildTintedKeepingOutline(base, tintColor, outline, match, flatTintNonMatch);
  tintedCanvasCache.set(key, built);
  return built;
}

export type CosmeticDrawOpts = {
  /**
   * @deprecated Cursor always rematches in forced modes; never skip.
   */
  skipCursorColor?: boolean;
  /** Live cursor fill (Color Rush / Heat / Settings). */
  liveCursorColor?: string | null;
  /** When true, also tint Hat / Face / Body to liveCursorColor. Cursor always rematches in forced modes. */
  tintAllCosmetics?: boolean;
  /** Forced-color mode (Color Rush / Heat / roulette white flash). */
  forceLiveCursorTint?: boolean;
  /** @deprecated use liveCursorColor + forceLiveCursorTint */
  tintColor?: string | null;
};

export function drawCosmeticOverlay(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  cursorRadius: number,
  cosmeticImage: HTMLImageElement | null,
  tintColor?: string | null,
) {
  if (!cosmeticImage || cosmeticImage.naturalWidth <= 0) return;
  if (!tintColor) {
    drawScaledCosmeticArt(ctx, cosmeticImage, x, y, cursorRadius, cosmeticImage.src || "img");
    return;
  }
  // Legacy flat tint path (whole image) — callers that need outline safety use drawLayerWithLiveTint.
  const { size, dx, dy } = cosmeticDrawBox(x, y, cursorRadius);
  const tinted = document.createElement("canvas");
  tinted.width = cosmeticImage.naturalWidth || COSMETIC_ART_SIZE;
  tinted.height = cosmeticImage.naturalHeight || COSMETIC_ART_SIZE;
  const tctx = tinted.getContext("2d")!;
  tctx.clearRect(0, 0, tinted.width, tinted.height);
  tctx.drawImage(cosmeticImage, 0, 0);
  tctx.globalCompositeOperation = "source-in";
  tctx.fillStyle = tintColor;
  tctx.fillRect(0, 0, tinted.width, tinted.height);
  tctx.globalCompositeOperation = "source-over";
  const scaled = scaleCosmeticArt(tinted, size, `${cosmeticImage.src}|flat|${tintColor}`);
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(scaled, dx, dy);
}

function currentAnimFrameIndex(frameCount: number, nowMs = performance.now()): number {
  if (frameCount <= 1) return 0;
  return Math.floor(nowMs / ANIM_FRAME_MS) % frameCount;
}

function drawLayerWithLiveTint(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  cursorRadius: number,
  layer: EquippedCosmeticLayer,
  opts: CosmeticDrawOpts,
) {
  const live = opts.liveCursorColor || opts.tintColor || null;
  const force = !!(opts.forceLiveCursorTint || opts.tintColor);
  const isForcedCursor = isCursorLiveTintCategory(layer.category);
  const shouldRematch =
    !!live && force && (isForcedCursor || !!opts.tintAllCosmetics);

  const baseSrc =
    shouldRematch && layer.sourceImageData ? layer.sourceImageData : layer.imageData;
  const matchSrc = shouldRematch ? layer.matchCursorMaskData : null;
  // Cursor: always flat-rematch non-outline fill when no match mask. Hat/Face/Body: same when Settings on.
  const flatNonMatch =
    shouldRematch && force && isForcedCursor && !layer.matchCursorMaskData
      ? true
      : shouldRematch && force && !!opts.tintAllCosmetics && !isForcedCursor;

  if (shouldRematch && live) {
    const tintKey = `${baseSrc}|${live}|${layer.outlineMaskData || ""}|${matchSrc || ""}|${
      flatNonMatch || (force && isForcedCursor && !matchSrc) ? 1 : 0
    }`;
    const tinted = getTintedLayerCanvas(
      baseSrc,
      live,
      layer.outlineMaskData,
      matchSrc,
      !!(flatNonMatch || (force && isForcedCursor && !matchSrc)),
    );
    if (tinted) {
      drawScaledCosmeticArt(ctx, tinted, x, y, cursorRadius, tintKey);
    } else {
      const img = getCachedCosmeticImage(layer.imageData);
      if (img) drawScaledCosmeticArt(ctx, img, x, y, cursorRadius, layer.imageData);
    }
  } else {
    const img = getCachedCosmeticImage(layer.imageData);
    if (img) drawScaledCosmeticArt(ctx, img, x, y, cursorRadius, layer.imageData);
  }

  const frames =
    shouldRematch && layer.sourceAnimationFrames?.length
      ? layer.sourceAnimationFrames
      : layer.animationFrames;
  if (!frames?.length) return;
  const idx = currentAnimFrameIndex(frames.length);
  const frameSrc = frames[idx];
  const frameMatch = shouldRematch ? layer.matchCursorMaskFrames?.[idx] ?? null : null;
  if (shouldRematch && live) {
    const frameFlat = !!(force && isForcedCursor && !frameMatch);
    const frameTintKey = `${frameSrc}|${live}||${frameMatch || ""}|${frameFlat ? 1 : 0}`;
    const tinted = getTintedLayerCanvas(
      frameSrc,
      live,
      null, // outline lives on static layer
      frameMatch,
      frameFlat,
    );
    if (tinted) {
      drawScaledCosmeticArt(ctx, tinted, x, y, cursorRadius, frameTintKey);
    } else {
      const fimg = getCachedCosmeticImage(frameSrc);
      if (fimg) drawScaledCosmeticArt(ctx, fimg, x, y, cursorRadius, frameSrc);
    }
  } else {
    const fimg = getCachedCosmeticImage(frameSrc);
    if (fimg) drawScaledCosmeticArt(ctx, fimg, x, y, cursorRadius, frameSrc);
  }
}

export function drawEquippedCosmeticOverlays(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  cursorRadius: number,
  opts?: CosmeticDrawOpts,
) {
  const o = opts || {};
  for (const layer of equippedCosmeticLayers) {
    // Cursor always rematches in forced modes; Hat/Face/Body follow Settings.
    drawLayerWithLiveTint(ctx, x, y, cursorRadius, layer, o);
  }
}

/** Draw cosmetics for a list of shop item ids (multiplayer remotes). */
export function drawCosmeticOverlaysForIds(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  cursorRadius: number,
  ids: string[] | null | undefined,
  opts?: CosmeticDrawOpts,
) {
  if (!ids?.length) return;
  const o = opts || {};
  for (const id of ids) {
    const layer = shopCosmeticCatalog.get(id);
    // Banners are profile-only; trails use a separate draw path; themes are UI-only.
    if (
      !layer ||
      layer.category === "banner" ||
      layer.category === "trail" ||
      layer.category === "theme"
    )
      continue;
    drawLayerWithLiveTint(ctx, x, y, cursorRadius, layer, o);
  }
}

/** Equipped ids safe to send/draw in multiplayer & lootdrops (no banners or themes). */
export function filterPlayCosmeticIds(ids: string[] | null | undefined): string[] {
  if (!ids?.length) return [];
  const out: string[] = [];
  for (const id of ids) {
    if (typeof id !== "string" || !id) continue;
    const layer = shopCosmeticCatalog.get(id);
    if (layer?.category === "banner" || layer?.category === "theme") continue;
    out.push(id);
    if (out.length >= 16) break;
  }
  return out;
}

/** Draw base cursor + optional cosmetic overlay centered on cursor. */
export function drawCursorWithCosmetic(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  cursorRadius: number,
  cursorColor: string,
  cosmeticImage: HTMLImageElement | null,
  opts: {
    showBase?: boolean;
    cosmeticOnly?: boolean;
    cosmeticScale?: number;
    animationFrames?: string[] | null;
  } = {},
) {
  const showBase = opts.showBase !== false && !opts.cosmeticOnly;
  if (showBase) {
    ctx.save();
    ctx.fillStyle = cursorColor;
    ctx.beginPath();
    ctx.arc(x, y, cursorRadius, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
  if (cosmeticImage && cosmeticImage.naturalWidth > 0) {
    drawScaledCosmeticArt(
      ctx,
      cosmeticImage,
      x,
      y,
      cursorRadius,
      cosmeticImage.src || "cosmetic",
      opts.cosmeticScale ?? 1,
    );
  }
  const frames = opts.animationFrames;
  if (frames?.length) {
    const idx = currentAnimFrameIndex(frames.length);
    const fimg = getCachedCosmeticImage(frames[idx]);
    if (fimg) {
      drawScaledCosmeticArt(
        ctx,
        fimg,
        x,
        y,
        cursorRadius,
        frames[idx],
        opts.cosmeticScale ?? 1,
      );
    }
  }
}

export async function drawCursorWithCosmeticAsync(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  cursorRadius: number,
  cursorColor: string,
  imageData: string | null | undefined,
  opts: {
    showBase?: boolean;
    cosmeticOnly?: boolean;
    cosmeticScale?: number;
    animationFrames?: string[] | null;
  } = {},
) {
  const img = imageData ? await loadCosmeticImage(imageData) : null;
  if (opts.animationFrames?.length) {
    await Promise.all(opts.animationFrames.map((f) => loadCosmeticImage(f)));
  }
  drawCursorWithCosmetic(ctx, x, y, cursorRadius, cursorColor, img, opts);
}
